import { ApiError } from '../../lib/api';
import { haptic } from '../../lib/haptics';
import type { ToastApi } from './Toast';

// The one shape of a user-triggered write (and the odd toasted read): optionally
// close the pane first (the save runs in the background — the toast provider and
// the SWR caches live above the modal, so they outlive the close), run the
// call(s), toast the outcome, and NEVER error-toast a 401 — api() has already
// redirected to login, so an "it failed" toast would just flash over the bounce.
export interface WriteWithToastOpts {
  /** The api call(s), plus any post-write revalidation that must precede the toast. */
  write: () => Promise<unknown>;
  /** Success toast. Omitted → no toast (e.g. a modal-open read that only toasts failure). */
  success?: string;
  /** Error toast — a string, or a builder reading the caught error (server messages). */
  error: string | ((e: unknown) => string);
  /** Close the pane instantly, BEFORE the write starts (the close-first pattern). */
  closeFirst?: () => void;
  /** After the success toast — bust caches, refresh lists, close late-closing panes. */
  onSaved?: () => void;
  /** On ANY failure, 401 included — reset latches so a retry is allowed. */
  onError?: (e: unknown) => void;
  /** On a REAL failure only (not the 401 redirect) — roll back the optimistic state. */
  revert?: () => void;
}

export function writeWithToast(toast: ToastApi, opts: WriteWithToastOpts): Promise<void> {
  opts.closeFirst?.();
  return (async () => {
    try {
      await opts.write();
      // Haptics ride on the toast, not on the write. A caller that passes no
      // success message has judged this write not worth reporting — Arrived,
      // Paid, a booking saved all do report; a background revalidation does
      // not — and a buzz would be reporting it. That one condition is why this
      // belongs here rather than at seven call sites, each free to disagree.
      if (opts.success) {
        toast.success(opts.success);
        haptic('ok');
      }
      opts.onSaved?.();
    } catch (e) {
      opts.onError?.(e);
      if (e instanceof ApiError && e.status === 401) return; // already redirected to login
      opts.revert?.();
      toast.error(typeof opts.error === 'function' ? opts.error(e) : opts.error);
      // Distinctly NOT the success pattern. A save that failed while the pane
      // was already closing is exactly the case where the screen is least able
      // to tell you.
      haptic('fail');
    }
  })();
}
